import type {
  ConnectorStripElement,
  FbaCoverageDashboardElement,
  SkuTwoPathCardElement,
} from '../../../demo-elements/types'
import type { DemoChatScript } from '../../../types'

// Always-on data-sources strip for Magic Science. All four of Nolan's
// systems connected from the start so the open shot lands on "the system
// runs on your stack." Uses the strip in data-sources mode so the
// failure-probability footer (default for the predictive-maintenance use
// case) is hidden.
const CONNECTOR_STRIP: ConnectorStripElement = {
  kind: 'connector-strip',
  id: 'strip-magic-science',
  mode: 'data-sources',
  asset: 'Magic Science · Minneapolis MN',
  sources: (['Fishbowl', 'Amazon', 'Excel', 'QuickBooks'] as const).map(
    (name) => ({ name, connected: true }),
  ),
  prediction: {
    // Ignored in data-sources mode; included to satisfy the type.
    probability: null,
    confidence: 'Unknown',
  },
}

// The FBA coverage table. Only the 5 at-risk SKUs render as full rows;
// the 15 healthy ones are summarized in the footer so the card doesn't
// take multiple screens of vertical space. The "20 SKUs reconciled"
// framing still lives in the title and headline.
const FBA_DASHBOARD: FbaCoverageDashboardElement = {
  kind: 'fba-coverage-dashboard',
  id: 'fba-dashboard',
  title: 'Magic Science — Amazon FBA Coverage — Last 30 days — 20 SKUs',
  headline:
    '5 SKUs at Amazon low-inventory-fee risk this week — ~$2,400 estimated exposure',
  footer: '15 SKUs on pace · last reconciled 2 min ago',
  rows: [
    {
      sku: 'MS-HCL-001',
      product: 'Pure HOCl Facial Mist 4oz',
      status: 'red',
      onHandFishbowl: 1_140,
      inTransit: 220,
      onHandAmazon: 340,
      daysOfCoverage: 9,
      action: 'Run 1,200 by Thu 5/21',
    },
    {
      sku: 'MS-DIS-001',
      product: 'Surface Disinfectant Spray 16oz',
      status: 'red',
      onHandFishbowl: 820,
      inTransit: 0,
      onHandAmazon: 210,
      daysOfCoverage: 7,
      action: 'Run 900 by Wed 5/20',
    },
    {
      sku: 'MS-HCL-002',
      product: 'Pure HOCl Facial Mist 8oz',
      status: 'yellow',
      onHandFishbowl: 940,
      inTransit: 180,
      onHandAmazon: 290,
      daysOfCoverage: 14,
      action: 'Run 800 by Mon 5/25',
    },
    {
      sku: 'MS-HCL-010',
      product: 'Wound Cleanser 8oz',
      status: 'yellow',
      onHandFishbowl: 610,
      inTransit: 0,
      onHandAmazon: 220,
      daysOfCoverage: 17,
      action: 'Run 600 by Wed 5/27',
    },
    {
      sku: 'MS-HCL-022',
      product: 'Pet Wound Spray 4oz',
      status: 'yellow',
      onHandFishbowl: 540,
      inTransit: 0,
      onHandAmazon: 180,
      daysOfCoverage: 19,
      action: 'Run 500 by Fri 5/29',
    },
  ],
}

// MS-HCL-001 drill-down. KPI tiles + two stacked horizontal timelines
// (problem then fix), then an inline demand-swing slider. Two
// scenarios authored: baseline (burn 62/day) and +30% velocity (burn
// 80.6/day → band entry 1 day earlier, stockout 1 day earlier, Neo
// recommends launching the production run Mon 5/19 instead of Thu).
// Shared 12-day axis from Today (Sun 5/17). Modeled band ≤280.
const SKU_TWO_PATH: SkuTwoPathCardElement = {
  kind: 'sku-two-path-card',
  id: 'twopath-mshcl001',
  title: 'MS-HCL-001 · Pure HOCl Facial Mist 4oz',
  windowDays: 12,
  todayLabel: 'Today · 5/17',
  sliderMin: -50,
  sliderMax: 50,
  sliderStep: 10,
  sliderLabel: 'Demand swing scenario',
  scenarios: [
    {
      value: 0,
      label: 'Baseline',
      subtitle: 'Two paths from today (Sun 5/17) · burn 62 units/day',
      kpis: [
        {
          label: 'Amazon on-hand',
          value: '340',
          detail: '9 days of coverage',
          accent: 'critical',
        },
        {
          label: 'Fishbowl on-hand',
          value: '1,140',
          detail: 'available to ship',
          accent: 'neutral',
        },
        {
          label: 'In-transit',
          value: '220',
          detail: 'lands Wed 5/20',
          accent: 'warning',
        },
        {
          label: 'Modeled band entry',
          value: 'in 6 days',
          detail: 'at ≤280 units',
          accent: 'critical',
        },
      ],
      doNothing: {
        label: 'If we do nothing',
        tone: 'risk',
        milestones: [
          {
            when: 'Wed 5/20',
            what: 'In-transit 220 lands → Amazon at 498',
            severity: 'neutral',
            dayOffset: 3,
          },
          {
            when: 'Sun 5/24',
            what: 'Enters modeled low-inv-fee band (≤280)',
            severity: 'warning',
            dayOffset: 7,
          },
          {
            when: 'Wed 5/27',
            what: 'Amazon stockout — listing falls behind',
            severity: 'critical',
            dayOffset: 10,
          },
        ],
        impact: '~$580 fee exposure · stockout from 5/27',
      },
      withRecommendation: {
        label: 'With the recommended run',
        tone: 'safe',
        milestones: [
          {
            when: 'Thu 5/21',
            what: 'Production run launches — 1,200 units · ships Sat 5/23',
            severity: 'good',
            dayOffset: 4,
          },
          {
            when: 'Fri 5/29',
            what: 'Lands at Amazon — back above the modeled band',
            severity: 'good',
            dayOffset: 12,
          },
        ],
        impact: 'Clear of the modeled band for 47 days',
      },
    },
    {
      value: 30,
      label: '+30% velocity',
      subtitle: '+30% velocity scenario · burn 81 units/day',
      kpis: [
        {
          label: 'Amazon on-hand',
          value: '340',
          detail: '7 days of coverage',
          accent: 'critical',
        },
        {
          label: 'Fishbowl on-hand',
          value: '1,140',
          detail: 'available to ship',
          accent: 'neutral',
        },
        {
          label: 'In-transit',
          value: '220',
          detail: 'lands Wed 5/20',
          accent: 'warning',
        },
        {
          label: 'Modeled band entry',
          value: 'in 3 days',
          detail: 'at ≤280 units',
          accent: 'critical',
        },
      ],
      doNothing: {
        label: 'If we do nothing',
        tone: 'risk',
        milestones: [
          {
            when: 'Wed 5/20',
            what: 'In-transit 220 lands → Amazon at 498',
            severity: 'neutral',
            dayOffset: 3,
          },
          {
            when: 'Sat 5/23',
            what: 'Enters modeled low-inv-fee band (≤280)',
            severity: 'warning',
            dayOffset: 6,
          },
          {
            when: 'Tue 5/26',
            what: 'Amazon stockout — listing falls behind',
            severity: 'critical',
            dayOffset: 9,
          },
        ],
        impact: '~$780 fee exposure · stockout from 5/26',
      },
      withRecommendation: {
        label: 'With the recommended run',
        tone: 'safe',
        milestones: [
          {
            when: 'Mon 5/19',
            what: 'Production run launches early — 1,200 units · ships Wed 5/21',
            severity: 'good',
            dayOffset: 2,
          },
          {
            when: 'Wed 5/27',
            what: 'Lands at Amazon — clears the modeled band just in time',
            severity: 'good',
            dayOffset: 10,
          },
        ],
        impact: 'Clear of the modeled band · 2 days of margin',
      },
      alert: {
        tone: 'risk',
        text: 'Alternative: hold the Thu run and air-freight the 220 in-transit — ~$9–15K expedite event.',
      },
    },
  ],
  honestyCaption:
    "Band modeled from this SKU's sell-through velocity, historical fee events, and category benchmarks. Amazon's threshold isn't published; this is a modeled risk zone, not a deterministic one.",
}

/**
 * Act 1 — Amazon FBA Coverage walk-through.
 *
 * Four linear beats, each separated by `await-user-prompt`:
 *   1. Open shot — 20-SKU FBA coverage dashboard, 5 at risk.
 *   2. Drill-down — MS-HCL-001 two-path card with modeled risk band
 *      and the demand-swing slider (slider is interactive; voice for
 *      the +30% case fires in beat 3 after the user advances).
 *   3. +30% scenario — Neo narrates the recompute (coverage runway,
 *      band-entry shift, two viable production paths).
 *   4. Push-to-team — handoff to Wed 1pm SIOP with the approval card.
 */
export const amazonFbaCoverage: DemoChatScript = {
  id: 'amazon-fba-coverage',
  name: 'Amazon FBA Coverage (Magic Science)',
  description:
    'Four beats: FBA coverage dashboard (20 SKUs, 5 at risk) → MS-HCL-001 drill-down with modeled risk band and demand-swing slider → +30% velocity scenario recompute → push the production run to Wed 1pm SIOP.',
  triggers: [
    'show me the system',
    'walk me through fba',
    "what's at risk this week",
    'low-inv risk',
    'ms-hcl-001',
  ],
  events: [
    {
      type: 'status',
      delayMs: 200,
      message:
        'Reconciling Fishbowl, Amazon Seller Central, and the workbook...',
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        'Reconciling on-hand from Fishbowl, in-transit and warehouse counts from Amazon, and velocity from the demand-plan workbook across 20 FBA SKUs.\n',
    },
    {
      type: 'thinking-delta',
      delayMs: 300,
      delta:
        "Modeling each SKU's distance to the Amazon low-inv-fee band from sell-through, historical fee events, and category benchmarks. Amazon's threshold isn't published; this is a modeled risk zone, not a deterministic one.\n",
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'query_fishbowl' },
    { type: 'tool-call', delayMs: 300, name: 'query_amazon_seller_central' },
    { type: 'tool-call', delayMs: 300, name: 'read_demand_plan_workbook' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        '20 Amazon FBA SKUs reconciled. 5 are tracking into the modeled low-inv-fee band this week.',
    },
    { type: 'demo-element-add', delayMs: 500, element: CONNECTOR_STRIP },
    { type: 'demo-element-add', delayMs: 400, element: FBA_DASHBOARD },
    {
      type: 'demo-element-add',
      delayMs: 500,
      element: {
        kind: 'text-block',
        id: 'fba-beat1-closer',
        text: 'Worst of the five: **MS-HCL-001 — Pure HOCl Facial Mist 4oz**, 9 days of coverage, modeled band entry in 6 days. **Want to drill in?**',
      },
    },
    { type: 'done', delayMs: 300, title: 'FBA coverage — open shot' },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 2: drill into the hero SKU ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Pulling 90-day velocity for MS-HCL-001...',
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        'Joining last 30 days of Amazon on-hand with current Fishbowl + in-transit and the demand-plan workbook to project the next 60 days.\n',
    },
    {
      type: 'thinking-delta',
      delayMs: 300,
      delta:
        "Modeling distance to Amazon's low-inv-fee band from this SKU's sell-through velocity, historical fee events, and category benchmarks.\n",
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'query_amazon_on_hand_history' },
    { type: 'tool-call', delayMs: 300, name: 'forecast_velocity' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        'On-hand 1,140 (Fishbowl), 340 (Amazon), 220 in-transit. Burn 62/day. 9 days of coverage.',
    },
    { type: 'demo-element-add', delayMs: 500, element: SKU_TWO_PATH },
    {
      type: 'demo-element-add',
      delayMs: 500,
      element: {
        kind: 'text-block',
        id: 'fba-beat2-closer',
        text: 'Recommended action: production run **1,200 units by Thu 5/21**. Risk band is modeled, not a published Amazon threshold. Drag the **demand-swing slider** above — **want me to walk through what +30% velocity does to this plan?**',
      },
    },
    { type: 'done', delayMs: 300, title: 'MS-HCL-001 — drill-down' },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 3: +30% velocity scenario reaction ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Recomputing coverage at +30% velocity...',
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        'Pulling the +30% velocity case from the demand-plan workbook and recomputing the coverage runway, modeled band-entry date, and the two viable production-timing paths.\n',
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'recompute_velocity_scenarios' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        '+30% velocity. Burn 62 → 81 units/day. Coverage drops 9 → 6 days.',
    },
    {
      type: 'text-delta',
      delayMs: 600,
      delta:
        ' Production trigger moves to Mon 5/19 — 2 days earlier than baseline. Alternative path holds Thursday and air-freights the 220 in-transit — a $9–15K expedite event.',
    },
    {
      type: 'demo-element-add',
      delayMs: 500,
      element: {
        kind: 'text-block',
        id: 'fba-beat3-closer',
        text: 'Two viable paths. Decide by **Mon 5/19** to keep ground-shipping the cheaper one. **Ready to push the production run to the team?**',
      },
    },
    { type: 'done', delayMs: 300, title: 'MS-HCL-001 — +30% scenario' },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 4: push the production run to the team ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Drafting the handoff for Wed 1pm SIOP review...',
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        "Looking up the production-line owner and raw-materials buyer in Fishbowl, and pulling this week's SIOP agenda owner so the item lands on Wed 1pm SIOP, not a fresh meeting.\n",
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'query_fishbowl_owners' },
    { type: 'tool-call', delayMs: 300, name: 'read_siop_agenda' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        'Drafted. Pedro M. owns the line, Sarah K. has the raw materials, Wed 1pm SIOP is the next checkpoint.',
    },
    {
      type: 'demo-element-add',
      delayMs: 500,
      element: {
        kind: 'text-block',
        id: 'fba-beat4-summary',
        text:
          '**Production run — 1,200 units MS-HCL-001 by Thu 5/21**\n\n' +
          '- Line: **Pedro M.** (line supervisor)\n' +
          '- Raw materials: **Sarah K.** (buyer)\n' +
          '- Review: **Wed 1pm SIOP** agenda',
      },
    },
    {
      type: 'demo-element-add',
      delayMs: 500,
      element: {
        kind: 'text-block',
        id: 'fba-beat4-closer',
        text: 'Approve to queue the item for Wed 1pm SIOP — or pass.',
      },
    },
    {
      type: 'approval',
      delayMs: 500,
      proposal: {
        proposalId: 'demo-ms-act1-push-to-siop',
        toolName: 'queue_for_siop_review',
        actionType: 'production_run_handoff',
        description:
          'Queue production run — 1,200 units MS-HCL-001 by Thu 5/21 — for the Wed 1pm SIOP review. Notifies Pedro M. (line supervisor) and Sarah K. (raw-materials buyer).',
      },
    },
    { type: 'done', delayMs: 300, title: 'Push to SIOP — Wed 1pm' },
  ],
}
